import React, { useEffect, useState, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useRoleAccess } from '@/hooks/useRoleAccess'
import {
  behaviorLearningApi,
  BehaviorProfile,
  BehaviorProfileSummary,
  BehaviorProfilesFilters,
  BehaviorCategory,
  SimplifiedClassification,
  BehaviorLearningStatus,
  ReprocessStatus,
} from '@/services/monitoring'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  RefreshCw,
  Brain,
  Clock,
  Calendar,
  CalendarClock,
  MoreVertical,
  X,
  Zap,
  Search,
  Loader2,
  Info,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Lock,
} from 'lucide-react'
import { toast } from 'sonner'

// Simplified classification display config (new system)
const CLASSIFICATION_CONFIG: Record<SimplifiedClassification, { label: string; color: string; icon: React.ReactNode; description: string }> = {
  hourly: {
    label: 'Hourly',
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    icon: <Clock className="h-3 w-3" />,
    description: 'Data appeared during learning window (1-9 AM) - Collects every hour'
  },
  daily: {
    label: 'Daily',
    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    icon: <Calendar className="h-3 w-3" />,
    description: 'No data during learning window - Only reprocess (10AM D-1, 12PM D-2, 2PM D-3, 4PM D-8)'
  },
  not_yet_classified: {
    label: 'Learning',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    icon: <Brain className="h-3 w-3" />,
    description: 'Classification pending (1-9 AM learning window)'
  },
  async: {
    label: 'Async',
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    icon: <Zap className="h-3 w-3" />,
    description: 'Async segment (email/bucket/push) - Always hourly, skip classification'
  }
}

// Legacy category display config (old system - kept for backwards compatibility)
const CATEGORY_CONFIG: Record<BehaviorCategory, { label: string; color: string; icon: React.ReactNode; description: string }> = {
  learning: {
    label: 'Learning',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    icon: <Brain className="h-3 w-3" />,
    description: 'Gathering data (first 3+ days) - Every hour'
  },
  hourly: {
    label: 'Hourly',
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    icon: <Clock className="h-3 w-3" />,
    description: 'Data changes throughout day - Every hour'
  },
  daily: {
    label: 'Daily',
    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    icon: <Calendar className="h-3 w-3" />,
    description: 'Data appears at consistent hour - Once at optimal hour + reprocess windows'
  },
  delayed: {
    label: 'Delayed',
    color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
    icon: <CalendarClock className="h-3 w-3" />,
    description: 'D0 never has data - Only at reprocess windows (D-1+)'
  },
  d1_only: {
    label: 'Delayed',  // Legacy name, display as Delayed
    color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
    icon: <CalendarClock className="h-3 w-3" />,
    description: 'D0 never has data - Only at reprocess windows (D-1+)'
  },
  async: {
    label: 'Async',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    icon: <Zap className="h-3 w-3" />,
    description: 'Async segment (email/bucket/push) - Always hourly, skip classification'
  }
}

const CLASSIFICATION_OPTIONS: SimplifiedClassification[] = ['hourly', 'daily', 'not_yet_classified', 'async']
const CATEGORY_OPTIONS: BehaviorCategory[] = ['hourly', 'daily', 'async']

// Helper to get the display config for a profile based on whether simplified scheduler is enabled
function getProfileDisplayConfig(profile: BehaviorProfile, simplifiedEnabled: boolean) {
  if (simplifiedEnabled && profile.classification) {
    return CLASSIFICATION_CONFIG[profile.classification] || CLASSIFICATION_CONFIG.not_yet_classified
  }
  // Fall back to legacy category
  const category = profile.behavior_category || 'learning'
  return CATEGORY_CONFIG[category] || CATEGORY_CONFIG.learning
}

interface ActiveFilters {
  // Simplified scheduler filters (new)
  classifications: SimplifiedClassification[]
  // Legacy filters (old system)
  categories: BehaviorCategory[]
  integrators: string[]
  rate_limited: 'all' | 'limited' | 'not_limited'
  has_override: 'all' | 'yes' | 'no'
  // Fingerprint learning filters (deprecated)
  reprocess_status: 'all' | ReprocessStatus
  availability: 'all' | 'same_day' | 'delayed' | 'very_delayed'
  has_recent_fingerprint: 'all' | 'yes' | 'no'
}

// Import JobsFilter type for cross-tab navigation
export interface JobsFilter {
  batchTimestamp?: string
  filterType?: 'hourly' | 'daily' | 'reprocess' | 'backfill' | 'email' | 'total' | 'completed' | 'pending' | 'running' | 'errors'
  batchLabel?: string
  integrationId?: number
  integrationName?: string
}

interface BehaviorLearningProps {
  onSmartFilter?: (filter: JobsFilter) => void
  organizationId?: number
}

/**
 * Behavior Learning Monitor Tab
 *
 * Shows behavior profiles for all integrations, allowing admins to:
 * - View learned behavior categories
 * - Monitor rate limiting
 * - Force category overrides
 * - Toggle the behavior learning kill switch
 */
export function BehaviorLearning({ onSmartFilter, organizationId }: BehaviorLearningProps) {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const { hasRole } = useRoleAccess()
  const [profiles, setProfiles] = useState<BehaviorProfile[]>([])
  const [summary, setSummary] = useState<BehaviorProfileSummary | null>(null)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [availableIntegrators, setAvailableIntegrators] = useState<string[]>([])

  // Kill switch state
  const [learningEnabled, setLearningEnabled] = useState(true)
  const [learningStatus, setLearningStatus] = useState<BehaviorLearningStatus | null>(null)
  const [learningLoading, setLearningLoading] = useState(false)
  const [showDisableDialog, setShowDisableDialog] = useState(false)

  // Filters - categories start with all selected (exclude mode)
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({
    classifications: [...CLASSIFICATION_OPTIONS], // All selected by default (new system)
    categories: [...CATEGORY_OPTIONS], // All selected by default (legacy)
    integrators: [],
    rate_limited: 'all',
    has_override: 'all',
    reprocess_status: 'all',
    availability: 'all',
    has_recent_fingerprint: 'all',
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Pagination
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)

  // Sorting
  type SortField = 'integration_id' | 'integration_name' | 'pattern_confidence' | 'availability_lag_days' | 'anomaly_count' | 'days_analyzed'
  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Action states
  const [actionInProgress, setActionInProgress] = useState<Record<number, boolean>>({})

  // Prevent double fetch in StrictMode
  const hasFetched = useRef(false)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Initial fetch
  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true
    fetchData()
    fetchLearningStatus()
  }, [])

  // Refetch when filters change
  useEffect(() => {
    if (!hasFetched.current) return
    fetchProfiles()
    fetchSummary()
  }, [activeFilters, debouncedSearch, page, organizationId])

  const fetchData = async () => {
    await Promise.all([fetchProfiles(), fetchSummary(), fetchIntegrators()])
  }

  const fetchIntegrators = async () => {
    try {
      const response = await behaviorLearningApi.getIntegrators()
      const slugs = response.map((i: { slug: string }) => i.slug)
      slugs.sort((a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase()))
      setAvailableIntegrators(slugs)
      // Initialize with all integrators selected (exclude mode)
      setActiveFilters(prev => ({ ...prev, integrators: slugs }))
    } catch (err: any) {
      console.error('Failed to fetch integrators:', err)
    }
  }

  const fetchProfiles = async () => {
    try {
      setLoading(true)
      setError(null)

      const filters: BehaviorProfilesFilters = {
        page,
        per_page: perPage,
        organization_id: organizationId,
      }

      // Category filter: if all selected, no filter. If some deselected, use single API filter or client-side
      const allCategoriesSelected = activeFilters.categories.length === CATEGORY_OPTIONS.length
      if (!allCategoriesSelected && activeFilters.categories.length === 1) {
        // API expects 'classification' parameter for the simplified scheduler
        filters.classification = activeFilters.categories[0] as SimplifiedClassification
      }
      // Note: if 2-3 categories selected, we filter client-side after fetch
      // Apply rate limited filter
      if (activeFilters.rate_limited === 'limited') {
        filters.rate_limited = true
      } else if (activeFilters.rate_limited === 'not_limited') {
        filters.rate_limited = false
      }
      // Apply override filter
      if (activeFilters.has_override === 'yes') {
        filters.has_override = true
      } else if (activeFilters.has_override === 'no') {
        filters.has_override = false
      }
      // Apply integrator filter (use first selected for API)
      if (activeFilters.integrators.length === 1) {
        filters.integrator = activeFilters.integrators[0]
      }
      if (debouncedSearch) {
        filters.search = debouncedSearch
      }
      // Fingerprint learning filters
      if (activeFilters.reprocess_status !== 'all') {
        filters.reprocess_status = activeFilters.reprocess_status
      }
      if (activeFilters.availability !== 'all') {
        filters.availability = activeFilters.availability
      }
      if (activeFilters.has_recent_fingerprint === 'yes') {
        filters.has_recent_fingerprint = true
      } else if (activeFilters.has_recent_fingerprint === 'no') {
        filters.has_recent_fingerprint = false
      }

      const response = await behaviorLearningApi.getProfiles(filters)
      console.log('Behavior profiles API response:', response)

      let filteredProfiles = response?.profiles || []

      // Client-side category filtering when 2-3 categories selected (API only supports single)
      if (!allCategoriesSelected && activeFilters.categories.length > 1) {
        filteredProfiles = filteredProfiles.filter(p =>
          activeFilters.categories.includes(p.behavior_category as BehaviorCategory)
        )
      }

      // Client-side integrator filtering (exclude mode: only show selected integrators)
      // If none selected, show nothing. If all selected, show all. Otherwise filter.
      const allIntegratorsSelected = activeFilters.integrators.length === availableIntegrators.length
      if (!allIntegratorsSelected) {
        filteredProfiles = filteredProfiles.filter(p =>
          activeFilters.integrators.includes(p.integrator_slug)
        )
      }

      setProfiles(filteredProfiles)
      setTotal(allCategoriesSelected && allIntegratorsSelected
        ? (response?.total || 0)
        : filteredProfiles.length)
    } catch (err: any) {
      console.error('Failed to fetch behavior profiles:', err)
      console.error('Response data:', err.response?.data)
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to load behavior profiles'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const fetchSummary = async () => {
    try {
      const data = await behaviorLearningApi.getSummary(organizationId)
      console.log('Behavior summary API response:', data)
      setSummary(data)
    } catch (err: any) {
      console.error('Failed to fetch summary:', err)
      console.error('Summary response data:', err.response?.data)
    }
  }

  const fetchLearningStatus = async () => {
    try {
      const status = await behaviorLearningApi.getStatus()
      console.log('Behavior learning status API response:', status)
      setLearningStatus(status)
      setLearningEnabled(status?.enabled ?? true)
    } catch (err: any) {
      console.error('Failed to fetch learning status:', err)
      console.error('Status response data:', err.response?.data)
      setLearningEnabled(true) // Default to enabled
    }
  }

  const handleToggleChange = (checked: boolean) => {
    if (!checked) {
      setShowDisableDialog(true)
    } else {
      handleEnableLearning()
    }
  }

  const handleDisableConfirm = async () => {
    setLearningLoading(true)
    try {
      const status = await behaviorLearningApi.disable('Manual disable from monitoring UI')
      setLearningStatus(status)
      setLearningEnabled(false)
      toast.success('Behavior learning disabled - all integrations will collect hourly')
    } catch (err) {
      console.error('Failed to disable behavior learning:', err)
      toast.error('Failed to disable behavior learning')
    } finally {
      setLearningLoading(false)
      setShowDisableDialog(false)
    }
  }

  const handleEnableLearning = async () => {
    setLearningLoading(true)
    try {
      const status = await behaviorLearningApi.enable()
      setLearningStatus(status)
      setLearningEnabled(true)
      toast.success('Behavior learning enabled')
    } catch (err) {
      console.error('Failed to enable behavior learning:', err)
      toast.error('Failed to enable behavior learning')
    } finally {
      setLearningLoading(false)
    }
  }

  const handleRefresh = () => {
    fetchData()
    fetchLearningStatus()
  }

  const handleSetOverride = async (profile: BehaviorProfile, category: BehaviorCategory) => {
    setActionInProgress(prev => ({ ...prev, [profile.id]: true }))
    try {
      await behaviorLearningApi.setOverride(profile.id, category)
      toast.success(`Override set to ${CATEGORY_CONFIG[category].label}`)
      fetchData()
    } catch (err) {
      console.error('Failed to set override:', err)
      toast.error('Failed to set override')
    } finally {
      setActionInProgress(prev => ({ ...prev, [profile.id]: false }))
    }
  }

  const handleClearOverride = async (profile: BehaviorProfile) => {
    setActionInProgress(prev => ({ ...prev, [profile.id]: true }))
    try {
      await behaviorLearningApi.clearOverride(profile.id)
      toast.success('Override cleared')
      fetchData()
    } catch (err) {
      console.error('Failed to clear override:', err)
      toast.error('Failed to clear override')
    } finally {
      setActionInProgress(prev => ({ ...prev, [profile.id]: false }))
    }
  }

  const handleFilterChange = (
    filterKey: 'categories' | 'integrators',
    value: string,
    checked: boolean
  ) => {
    setActiveFilters(prev => {
      const currentValues = prev[filterKey] as string[]
      if (checked) {
        return { ...prev, [filterKey]: [...currentValues, value] }
      } else {
        return { ...prev, [filterKey]: currentValues.filter(v => v !== value) }
      }
    })
    setPage(1)
  }

  const clearFilter = (filterKey: keyof ActiveFilters) => {
    if (filterKey === 'categories' || filterKey === 'integrators') {
      setActiveFilters(prev => ({ ...prev, [filterKey]: [] }))
    } else {
      setActiveFilters(prev => ({ ...prev, [filterKey]: 'all' }))
    }
    setPage(1)
  }

  const clearAllFilters = () => {
    setActiveFilters({
      classifications: [...CLASSIFICATION_OPTIONS], // Reset to all selected (new system)
      categories: [...CATEGORY_OPTIONS], // Reset to all selected
      integrators: [...availableIntegrators], // Reset to all selected
      rate_limited: 'all',
      has_override: 'all',
      reprocess_status: 'all',
      availability: 'all',
      has_recent_fingerprint: 'all',
    })
    setSearchQuery('')
    setPage(1)
  }

  const hasActiveFilters = () => {
    return (
      activeFilters.categories.length < CATEGORY_OPTIONS.length || // Some categories unchecked
      activeFilters.integrators.length < availableIntegrators.length || // Some integrators unchecked
      activeFilters.rate_limited !== 'all' ||
      activeFilters.has_override !== 'all' ||
      activeFilters.reprocess_status !== 'all' ||
      activeFilters.availability !== 'all' ||
      activeFilters.has_recent_fingerprint !== 'all' ||
      searchQuery !== ''
    )
  }

  // Handle column sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction or clear sort
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else {
        setSortField(null)
        setSortDirection('asc')
      }
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Get sorted profiles
  const sortedProfiles = React.useMemo(() => {
    if (!sortField) return profiles

    return [...profiles].sort((a, b) => {
      let aVal = a[sortField]
      let bVal = b[sortField]

      // Handle nulls
      if (aVal === null || aVal === undefined) aVal = sortDirection === 'asc' ? Infinity : -Infinity
      if (bVal === null || bVal === undefined) bVal = sortDirection === 'asc' ? Infinity : -Infinity

      // Compare
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      }

      if (sortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0
      }
    })
  }, [profiles, sortField, sortDirection])

  // Sortable header component
  const SortableHeader = ({ field, children, className = '' }: { field: SortField; children: React.ReactNode; className?: string }) => (
    <div
      className={`flex items-center gap-1 cursor-pointer hover:text-foreground select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      {children}
      {sortField === field ? (
        sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-50" />
      )}
    </div>
  )

  const totalPages = Math.ceil(total / perPage)

  // Filter popup component (matching Errors table style)
  // Both categories and integrators use exclude mode: all selected = no filter, some unchecked = filter active
  const FilterPopup = ({
    label,
    filterKey,
    options,
    selectedValues,
    getLabel = (v: string) => v
  }: {
    label: string
    filterKey: 'categories' | 'integrators'
    options: string[]
    selectedValues: string[]
    getLabel?: (value: string) => string
  }) => {
    // Filter is active when NOT all are selected (some are filtered)
    const hasFilter = selectedValues.length > 0 && selectedValues.length < options.length

    // Show count of selected items (like Integrations page)
    const filterCount = selectedValues.length

    const handleSelectAll = () => {
      if (filterKey === 'categories') {
        setActiveFilters(prev => ({ ...prev, categories: [...CATEGORY_OPTIONS] }))
      } else {
        setActiveFilters(prev => ({ ...prev, integrators: [...availableIntegrators] }))
      }
      setPage(1)
    }

    const handleClear = () => {
      if (filterKey === 'categories') {
        setActiveFilters(prev => ({ ...prev, categories: [] }))
      } else {
        setActiveFilters(prev => ({ ...prev, integrators: [] }))
      }
      setPage(1)
    }

    const allSelected = selectedValues.length === options.length
    const noneSelected = selectedValues.length === 0

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`h-6 px-2 ${hasFilter ? 'bg-primary/10 text-primary' : ''}`}
          >
            <Filter className="h-3 w-3" />
            {hasFilter && <span className="ml-1 text-xs">{filterCount}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="start">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{label}</span>
            {noneSelected ? (
              <Button variant="ghost" size="sm" onClick={handleSelectAll} className="h-6 px-2 text-xs">
                All
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={handleClear} className="h-6 px-2 text-xs">
                Clear
              </Button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {options.length === 0 ? (
              <p className="text-sm text-muted-foreground">No options available</p>
            ) : (
              options.map((option) => {
                const isChecked = selectedValues.includes(option)
                const displayLabel = getLabel(option)

                return (
                  <div key={option} className="flex items-center space-x-2">
                    <Checkbox
                      id={`${filterKey}-${option}`}
                      checked={isChecked}
                      onCheckedChange={(checked) =>
                        handleFilterChange(filterKey, option, checked === true)
                      }
                    />
                    <Label
                      htmlFor={`${filterKey}-${option}`}
                      className="text-sm cursor-pointer truncate flex items-center gap-1"
                      title={displayLabel}
                    >
                      {filterKey === 'categories' && CATEGORY_CONFIG[option as BehaviorCategory]?.icon}
                      {displayLabel}
                    </Label>
                  </div>
                )
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  // Boolean filter popup (for rate_limited, has_override, and availability)
  const BooleanFilterPopup = ({
    label,
    filterKey,
    value,
    options,
  }: {
    label: string
    filterKey: 'rate_limited' | 'has_override' | 'availability'
    value: string
    options: { value: string; label: string }[]
  }) => {
    const hasFilter = value !== 'all'

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`h-6 px-2 ${hasFilter ? 'bg-primary/10 text-primary' : ''}`}
          >
            <Filter className="h-3 w-3" />
            {hasFilter && <span className="ml-1 text-xs">1</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-3" align="start">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{label}</span>
            {hasFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => clearFilter(filterKey)}
                className="h-6 px-2 text-xs"
              >
                Clear
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {options.map((option) => (
              <div key={option.value} className="flex items-center space-x-2">
                <Checkbox
                  id={`${filterKey}-${option.value}`}
                  checked={value === option.value}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setActiveFilters(prev => ({ ...prev, [filterKey]: option.value }))
                      setPage(1)
                    } else {
                      setActiveFilters(prev => ({ ...prev, [filterKey]: 'all' }))
                      setPage(1)
                    }
                  }}
                />
                <Label
                  htmlFor={`${filterKey}-${option.value}`}
                  className="text-sm cursor-pointer"
                >
                  {option.label}
                </Label>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5" />
                Behavior Learning Monitor
                {total > 0 && <Badge variant="secondary">{total}</Badge>}
              </CardTitle>
              <CardDescription>
                Integration collection frequency optimization based on learned patterns
              </CardDescription>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Clear Filters */}
              {hasActiveFilters() && (
                <Button variant="outline" size="sm" onClick={clearAllFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Clear Filters
                </Button>
              )}

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-48"
                />
              </div>

              {/* Refresh Button */}
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Summary Cards - Simplified: Hourly, Daily, Async, Overridden */}
          {summary && (
            <div className="grid grid-cols-4 gap-4">
              <SummaryCard
                title="Hourly"
                value={summary.hourly}
                color="yellow"
                icon={<Clock className="h-4 w-4" />}
                onClick={() => {
                  const onlyHourly = activeFilters.categories.length === 1 && activeFilters.categories[0] === 'hourly'
                  setActiveFilters(prev => ({
                    ...prev,
                    categories: onlyHourly ? [...CATEGORY_OPTIONS] : ['hourly']
                  }))
                  setPage(1)
                }}
                active={activeFilters.categories.length === 1 && activeFilters.categories[0] === 'hourly'}
              />
              <SummaryCard
                title="Daily"
                value={summary.daily}
                color="green"
                icon={<Calendar className="h-4 w-4" />}
                onClick={() => {
                  const onlyDaily = activeFilters.categories.length === 1 && activeFilters.categories[0] === 'daily'
                  setActiveFilters(prev => ({
                    ...prev,
                    categories: onlyDaily ? [...CATEGORY_OPTIONS] : ['daily']
                  }))
                  setPage(1)
                }}
                active={activeFilters.categories.length === 1 && activeFilters.categories[0] === 'daily'}
              />
              <SummaryCard
                title="Async"
                value={summary.async_segments}
                color="blue"
                icon={<Zap className="h-4 w-4" />}
                onClick={() => {
                  const onlyAsync = activeFilters.categories.length === 1 && activeFilters.categories[0] === 'async'
                  setActiveFilters(prev => ({
                    ...prev,
                    categories: onlyAsync ? [...CATEGORY_OPTIONS] : ['async']
                  }))
                  setPage(1)
                }}
                active={activeFilters.categories.length === 1 && activeFilters.categories[0] === 'async'}
              />
              <SummaryCard
                title="Overridden"
                value={summary.overridden}
                color="purple"
                icon={<Info className="h-4 w-4" />}
                onClick={() => {
                  setActiveFilters(prev => ({
                    ...prev,
                    has_override: prev.has_override === 'yes' ? 'all' : 'yes'
                  }))
                  setPage(1)
                }}
                active={activeFilters.has_override === 'yes'}
              />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="rounded-md bg-destructive/10 p-4 text-destructive">
              {error}
            </div>
          )}

          {/* Table */}
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">
                    <SortableHeader field="integration_id">ID</SortableHeader>
                  </TableHead>
                  <TableHead>
                    <SortableHeader field="integration_name">Integration</SortableHeader>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <span>Integrator</span>
                      <FilterPopup
                        label="Filter by Integrator"
                        filterKey="integrators"
                        options={availableIntegrators}
                        selectedValues={activeFilters.integrators}
                      />
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <span>Behavior</span>
                      <FilterPopup
                        label="Filter by Behavior"
                        filterKey="categories"
                        options={CATEGORY_OPTIONS}
                        selectedValues={activeFilters.categories}
                        getLabel={(cat) => CATEGORY_CONFIG[cat as BehaviorCategory]?.label || cat}
                      />
                    </div>
                  </TableHead>
                  <TableHead className="w-[100px]">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="cursor-help">Optimal Hour</TooltipTrigger>
                        <TooltipContent><p>First hour (1-9 AM) when data appeared during learning window</p></TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  <TableHead className="w-[150px]">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="cursor-help">Reprocess</TooltipTrigger>
                        <TooltipContent><p>Fixed reprocess schedule (10AM D-1, 12PM D-2, 2PM D-3, 4PM D-8)</p></TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : profiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No behavior profiles found
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedProfiles.map((profile) => {
                    // Async integrations always show as "Async" regardless of behavior_category
                    const effectiveCategory = profile.is_async
                      ? 'async' as BehaviorCategory
                      : (profile.forced_category || profile.behavior_category) as BehaviorCategory
                    const config = CATEGORY_CONFIG[effectiveCategory]

                    return (
                      <TableRow key={profile.id}>
                        <TableCell className="font-mono text-xs">
                          <Link
                            to={`/${orgSlug}/integrations/${profile.integration_id}/edit`}
                            className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            {profile.integration_id}
                          </Link>
                        </TableCell>
                        <TableCell className="font-medium max-w-[200px] truncate">
                          {profile.integration_name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">
                            {profile.integrator_slug}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {profile.forced_category && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Lock className="h-3.5 w-3.5 text-purple-500 shrink-0" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Forced to {CATEGORY_CONFIG[profile.forced_category as BehaviorCategory]?.label || profile.forced_category}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Badge className={`${config.color} gap-1`}>
                                      {config.icon}
                                      {config.label}
                                    </Badge>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{config.description}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          </div>
                        </TableCell>
                        {/* Optimal Hour */}
                        <TableCell className="text-center">
                          {profile.optimal_collection_hour ? (
                            <span className="text-sm font-medium">{profile.optimal_collection_hour}:00</span>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </TableCell>
                        {/* Reprocess Schedule */}
                        <TableCell>
                          <div className="flex flex-nowrap items-center gap-0.5 text-xs text-muted-foreground">
                            D-1, D-2, D-3, D-8
                          </div>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={actionInProgress[profile.id]}
                              >
                                {actionInProgress[profile.id] ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <MoreVertical className="h-4 w-4" />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Force Category</DropdownMenuLabel>
                              <DropdownMenuItem
                                onClick={() => handleSetOverride(profile, 'hourly')}
                                className="gap-2"
                              >
                                <Clock className="h-4 w-4 text-yellow-600" />
                                Force Hourly
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleSetOverride(profile, 'daily')}
                                className="gap-2"
                              >
                                <Calendar className="h-4 w-4 text-green-600" />
                                Force Daily
                              </DropdownMenuItem>
                              {profile.forced_category && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => handleClearOverride(profile)}
                                    className="gap-2 text-destructive"
                                  >
                                    <X className="h-4 w-4" />
                                    Clear Override
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {profiles.length} of {total} profiles
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                Page {page} of {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages || loading}
              >
                Next
              </Button>
            </div>
          </div>

        </CardContent>
      </Card>

    </>
  )
}

// Summary Card Component
interface SummaryCardProps {
  title: string
  value: number
  color: 'blue' | 'yellow' | 'green' | 'emerald' | 'red' | 'purple'
  icon: React.ReactNode
  onClick?: () => void
  active?: boolean
}

function SummaryCard({ title, value, color, icon, onClick, active }: SummaryCardProps) {
  const colorClasses = {
    blue: 'border-blue-500 bg-blue-50 dark:bg-blue-950/50',
    yellow: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950/50',
    green: 'border-green-500 bg-green-50 dark:bg-green-950/50',
    emerald: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/50',
    red: 'border-red-500 bg-red-50 dark:bg-red-950/50',
    purple: 'border-purple-500 bg-purple-50 dark:bg-purple-950/50',
  }

  const iconColorClasses = {
    blue: 'text-blue-600',
    yellow: 'text-yellow-600',
    green: 'text-green-600',
    emerald: 'text-emerald-600',
    red: 'text-red-600',
    purple: 'text-purple-600',
  }

  return (
    <button
      onClick={onClick}
      className={`
        p-4 rounded-lg border-2 transition-all text-left
        ${active ? colorClasses[color] : 'border-transparent hover:border-muted-foreground/20'}
        ${onClick ? 'cursor-pointer hover:scale-[1.02]' : ''}
      `}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={iconColorClasses[color]}>{icon}</span>
        <span className="text-sm text-muted-foreground">{title}</span>
      </div>
      <div className="text-2xl font-bold">{value.toLocaleString()}</div>
    </button>
  )
}

